import { Checkbox, FormControl, FormControlLabel, FormGroup } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { Fragment } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeTree } from 'react-vtree';

import type { ISelectProduct } from 'v2/interfaces/product/selectProduct';

import type { VariableSizeNodeComponentProps, VariableSizeNodeMetadata } from 'react-vtree';

const styles = makeStyles({
  checkbox: {
    padding: 0,
    marginRight: 8,
  },
  label: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: 480,
    maxWidth: '100%',
  },
});

type DataNode = {
  children: DataNode[];
  id: number;
  name: string;
  checked?: boolean;
};

type StackElement = {
  nestingLevel: number;
  node: DataNode;
};

type ExtendedData = {
  readonly isLeaf: boolean;
  readonly name: string;
  readonly nestingLevel: number;
  readonly onSelect: (id: any) => void;
  readonly checked?: boolean;
};

const createNode = (products: any = []) => {
  const node: DataNode = {
    children: [],
    id: 0,
    name: 'Produtos',
  };

  for (let i = 0; i < products.length; i++) {
    node.children.push(createNode2(products[i]));
  }

  return node;
};

const createNode2 = (product: any) => {
  const node: DataNode = {
    children: [],
    id: product.id,
    name: product.title,
    checked: product.checked,
  };

  const size = product.children ? product.children.length : 0;

  for (let i = 0; i < size; i++) {
    node.children.push(createNode2(product.children[i]));
  }

  return node;
};

const Node: React.FunctionComponent<VariableSizeNodeComponentProps<ExtendedData>> = ({
  data: { isLeaf, name, id, nestingLevel, onSelect, checked },
  style,
  treeData: itemSize,
}) => {
  if (id === '0') return null;

  const classes = styles({});

  const onClickSelect = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string | symbol) => {
    event.stopPropagation();
    onSelect(id);
  };

  const isChild = isLeaf && nestingLevel !== 1;
  const isParentWithChildren = nestingLevel === 1 && !isLeaf;

  const borderChild = '0px 1px 0px 1px';
  const borderParentWithChildren = '1px 1px 0px 1px';

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        padding: isChild ? '0px 0px 0px 32px' : 16,
        borderWidth: isChild ? borderChild : isParentWithChildren ? borderParentWithChildren : '1px',
        borderStyle: 'solid',
        borderColor: '#ddd',
        transform: `translateY(${-itemSize}px)`,
      }}
    >
      <FormControlLabel
        classes={{ label: classes.label }}
        control={
          <Checkbox
            checked={checked ? true : false}
            classes={{ root: classes.checkbox }}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onClickSelect(e, id)}
          />
        }
        label={`${String(id)} - ${name}`}
      />
    </div>
  );
};

interface ITreePresenterProps {
  itemSize: number;
  products: ISelectProduct[];
  selectedProducts?: string[];
  productsAndChildren?: ISelectProduct[];
  onSelect: (id: number) => void;
  handleClickSelectAll?: () => void;
}

const TreePresenter: React.FunctionComponent<ITreePresenterProps> = ({
  itemSize,
  products,
  onSelect,
  handleClickSelectAll,
  selectedProducts,
  productsAndChildren,
}) => {
  const tree = React.useRef<VariableSizeTree<ExtendedData>>(null);

  const treeWalker = React.useCallback(
    function* (refresh: boolean): Generator<VariableSizeNodeMetadata<ExtendedData> | string | symbol, void, boolean> {
      const stack: StackElement[] = [];

      stack.push({
        nestingLevel: 0,
        node: createNode(products || []),
      });

      while (stack.length !== 0) {
        const { node, nestingLevel } = stack.pop()!;
        const id = node && node.id ? node.id.toString() : '0';

        const isOpened = yield refresh
          ? {
              defaultHeight: itemSize,
              id,
              isLeaf: node.children.length === 0,
              isOpenByDefault: true,
              name: node.name,
              nestingLevel,
              onSelect,
              checked: node.checked,
            }
          : id;

        if (node.children.length !== 0 && isOpened) {
          // tslint:disable-next-line:increment-decrement
          for (let i = node.children.length - 1; i >= 0; i--) {
            stack.push({
              nestingLevel: nestingLevel + 1,
              node: node.children[i],
            });
          }
        }
      }
    },
    [itemSize, onSelect, products]
  );

  React.useEffect(() => {
    tree.current && tree.current.recomputeTree({ refreshNodes: true, useDefaultHeight: true });
  }, [itemSize, products]);

  return (
    <Fragment>
      {!!productsAndChildren?.length && (
        <div>
          <FormControl>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedProducts?.length === productsAndChildren?.length}
                    indeterminate={selectedProducts?.length > 0 && selectedProducts?.length < productsAndChildren?.length}
                    onClick={handleClickSelectAll}
                  />
                }
                label={`Selecionar todos os produtos (${productsAndChildren?.length})`}
              />
            </FormGroup>
          </FormControl>
        </div>
      )}

      <AutoSizer disableWidth>
        {({ height }) => (
          <VariableSizeTree<ExtendedData> ref={tree} itemData={itemSize} treeWalker={treeWalker} height={height - 100} width='100%'>
            {Node}
          </VariableSizeTree>
        )}
      </AutoSizer>
    </Fragment>
  );
};

export default TreePresenter;
